import "./IntakeUsersTable.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextArea } from "components/inputs";
import LoadingIcon from "components/Loading/LoadingIcon";
import KaboodleModal from "components/Modals/KaboodleModal";
import Table from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { useState } from "react";
import type { Column } from "react-data-grid";
import { Controller, useForm } from "react-hook-form";
import type { UserType } from "services/users-api";
import {
  useApproveUserMutation,
  useGetUsersQuery,
  useRejectUserMutation,
} from "services/users-api";
import { useToken } from "utils/context";

type RejectFormData = {
  reason: string;
};

const IntakeUsersTable = () => {
  const { accessToken } = useToken();
  const [userFilter, setUserFilter] = useState<"pending" | "approved">(
    "pending",
  );
  const [userToReject, setUserToReject] = useState<number | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RejectFormData>({
    defaultValues: {
      reason: "",
    },
    mode: "onBlur",
  });

  const {
    data: usersResponse,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetUsersQuery(
    {
      isPendingApproval: userFilter === "pending",
      userType: "INTAKE",
      isRejected: false,
      page: currentPage,
      pageSize: pageSize,
    },
    { skip: !accessToken },
  );

  const users = usersResponse?.results || [];
  const totalUsers = usersResponse?.count || 0;

  const [approveUser, { isLoading: isApproving }] = useApproveUserMutation();
  const [rejectUser, { isLoading: isRejecting }] = useRejectUserMutation();

  const handleApproveUser = async (userId: number) => {
    if (!accessToken || isApproving || isRejecting) return;
    try {
      await approveUser({ id: userId }).unwrap();
      KaboodleToaster.success("User approved successfully");
    } catch (error) {
      console.error("Failed to approve user:", error);
      KaboodleToaster.error("Failed to approve user");
    }
  };

  const openRejectModal = (userId: number) => {
    setUserToReject(userId);
    setRejectModalOpen(true);
    reset({ reason: "" });
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setUserToReject(null);
  };

  const submitRejectUser = handleSubmit(async (data) => {
    if (!accessToken || !userToReject || isRejecting || isApproving) return;

    try {
      await rejectUser({
        id: userToReject,
        reason: data.reason,
      }).unwrap();

      KaboodleToaster.success(
        userFilter === "pending" ? "User rejected" : "User approval revoked",
      );
      closeRejectModal();
    } catch (error) {
      console.error("Failed to reject user:", error);
      KaboodleToaster.error(
        userFilter === "pending"
          ? "Failed to reject user"
          : "Failed to revoke user approval",
      );
    }
  });

  const isActionInProgress = isApproving || isRejecting;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (filter: "pending" | "approved") => {
    setUserFilter(filter);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const commonColumns: Column<UserType>[] = [
    {
      key: "username",
      name: "Username",
      ...commonColumnProps,
    },
    {
      key: "email",
      name: "Email",
      ...commonColumnProps,
    },
  ];

  const pendingUserColumns = [
    ...commonColumns,
    {
      key: "actions",
      name: "Actions",
      ...commonColumnProps,
      width: "15%",
      renderCell: ({ row }) => (
        <div className="flex jc-c no-pd action-buttons">
          <div
            className="material-icons check_circle"
            style={{
              color: "green",
              opacity: isApproving ? 0.5 : 1,
              cursor: isActionInProgress ? "default" : "pointer",
            }}
            title={
              isApproving ? "Approval in progress..." : "Approve this user"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isActionInProgress) handleApproveUser(row.id);
            }}
          >
            check_circle
          </div>
          <div
            className="material-icons remove_circle"
            style={{
              color: "red",
              opacity: isRejecting ? 0.5 : 1,
              cursor: isActionInProgress ? "default" : "pointer",
              marginLeft: "12px",
            }}
            title={
              isRejecting ? "Rejection in progress..." : "Reject this user"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isActionInProgress) openRejectModal(row.id);
            }}
          >
            remove_circle
          </div>
        </div>
      ),
    },
  ];

  const approvedUserColumns = [
    ...commonColumns,
    {
      key: "actions",
      name: "Actions",
      ...commonColumnProps,
      width: "10%",
      renderCell: ({ row }) => (
        <div className="flex jc-c no-pd action-buttons">
          <div
            className="material-icons remove_circle"
            style={{
              color: "red",
              opacity: isRejecting ? 0.5 : 1,
              cursor: isActionInProgress ? "default" : "pointer",
            }}
            title={
              isRejecting
                ? "Revoke in progress..."
                : "Revoke approval for this user"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isActionInProgress) openRejectModal(row.id);
            }}
          >
            remove_circle
          </div>
        </div>
      ),
    },
  ];

  const columns =
    userFilter === "pending" ? pendingUserColumns : approvedUserColumns;

  if (isLoadingUsers) {
    return <LoadingIcon />;
  }

  if (usersError) {
    return <div className="error-message">Error loading users</div>;
  }

  return (
    <>
      <div className="intake-users-table card">
        <div className="card__header">
          Intake Users ({totalUsers}{" "}
          {userFilter === "pending" ? "pending" : "approved"})
        </div>
        <div className="filter-tabs">
          <button
            className={`filter-tab ${userFilter === "pending" ? "active" : ""}`}
            onClick={() => handleFilterChange("pending")}
          >
            Pending Approval
          </button>
          <button
            className={`filter-tab ${userFilter === "approved" ? "active" : ""}`}
            onClick={() => handleFilterChange("approved")}
          >
            Approved
          </button>
        </div>
        <Table
          columns={columns}
          rows={users}
          rowKeyGetter={(row) => row.id}
          emptyTableMessage={
            userFilter === "pending"
              ? "No intake users pending approval"
              : "No approved intake users found"
          }
          pagination={{
            pageSize: pageSize,
            pageIndex: currentPage,
            rowCount: totalUsers,
            onPageChange: handlePageChange,
            isLoading: isLoadingUsers,
          }}
          isLoading={isLoadingUsers}
        />
      </div>

      <KaboodleModal
        isOpen={rejectModalOpen}
        onClose={closeRejectModal}
        footer={
          <div className="flex jc-c">
            <Button
              className="delete"
              onClick={submitRejectUser}
              disabled={isRejecting}
            >
              {userFilter === "pending" ? "Reject User" : "Revoke Approval"}
            </Button>
            <Button onClick={closeRejectModal}>Cancel</Button>
          </div>
        }
      >
        <Card
          heading={
            userFilter === "pending" ? "Reject User" : "Revoke User Approval"
          }
        >
          <p>
            Please provide a reason for{" "}
            {userFilter === "pending" ? "rejecting" : "revoking approval for"}{" "}
            this user:
          </p>
          <Controller
            name="reason"
            control={control}
            rules={{ required: "Reason is required" }}
            render={({ field }) => (
              <TextArea
                {...field}
                ref={field.ref}
                label="Reason"
                errors={errors}
                rows={4}
              />
            )}
          />
        </Card>
      </KaboodleModal>
    </>
  );
};

export default IntakeUsersTable;
